import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Terminal, Settings, Code, Check, ChevronRight } from 'lucide-react';

/**
 * ProfileSelectorModal - Advanced profile selector with details view
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: () => void - Close handler
 * - onSelect: (profile) => void - Selection handler
 * - profiles: Array of ProfileData objects
 * - selectedProfile: Currently selected profile
 * - launcher: LauncherData object (for context)
 * - machineCapabilities: Array of capabilities for executable path lookup
 */
const ProfileSelectorModal = ({ 
  isOpen, 
  onClose, 
  onSelect,
  profiles = [],
  selectedProfile,
  launcher,
  machineCapabilities = []
}) => {
  // Track which profile is being previewed (highlighted in list)
  const [previewProfile, setPreviewProfile] = useState(null);

  // Initialize preview to selected profile when modal opens
  useEffect(() => {
    if (isOpen && selectedProfile) {
      setPreviewProfile(selectedProfile);
    }
  }, [isOpen, selectedProfile]);

  if (!isOpen) return null;

  // Get the profile to display details for
  const displayProfile = previewProfile || profiles[0];

  // Get executable path from capability if available
  const getExecutablePath = (profile) => {
    const capability = machineCapabilities.find(c => c.profile_id === profile?.profile_id);
    return capability?.executable_path || 
      profile?.windows_executable || 
      profile?.linux_executable || 
      profile?.macos_executable ||
      'Not specified';
  };

  // Handle selection
  const handleSelect = () => {
    if (previewProfile) {
      onSelect(previewProfile);
      onClose();
    }
  };

  // Format environment variables for display
  const envVars = displayProfile?.environment_vars || {};
  const hasEnvVars = Object.keys(envVars).length > 0;

  // Format launch arguments
  const launchArgs = displayProfile?.launch_args || [];
  const hasLaunchArgs = launchArgs.length > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Select Profile
            </h2>
            <p className="text-sm text-gray-400">
              {launcher?.display_name} • {profiles.length} profile{profiles.length !== 1 ? 's' : ''} available
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - split view */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Left Panel - Profile List */}
          <div className="w-64 flex-shrink-0 border-r border-gray-700 overflow-y-auto">
            <div className="p-2">
              {profiles.map(profile => (
                <button
                  key={profile.profile_id}
                  onClick={() => setPreviewProfile(profile)}
                  className={`
                    w-full px-3 py-3 rounded-lg text-left transition-all mb-1
                    flex items-center gap-3 group
                    ${previewProfile?.profile_id === profile.profile_id 
                      ? 'bg-blue-500/20 border border-blue-500/50' 
                      : 'hover:bg-gray-800 border border-transparent'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium truncate ${
                        previewProfile?.profile_id === profile.profile_id 
                          ? 'text-blue-400' 
                          : 'text-white'
                      }`}>
                        {profile.name}
                      </span>
                      {profile.is_default && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-700 text-gray-400">
                          default
                        </span>
                      )}
                    </div>
                    {profile.version && (
                      <span className="text-xs text-gray-500">v{profile.version}</span>
                    )}
                  </div>
                  {selectedProfile?.profile_id === profile.profile_id && (
                    <Check size={16} className="text-green-400 flex-shrink-0" />
                  )}
                  <ChevronRight 
                    size={16} 
                    className={`flex-shrink-0 transition-colors ${
                      previewProfile?.profile_id === profile.profile_id 
                        ? 'text-blue-400' 
                        : 'text-gray-600 group-hover:text-gray-400'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right Panel - Profile Details */}
          <div className="flex-1 overflow-y-auto p-6">
            {displayProfile ? (
              <div className="space-y-6">
                {/* Profile Header */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {displayProfile.name}
                  </h3>
                  <div className="flex items-center gap-3 text-sm">
                    {displayProfile.version && (
                      <span className="text-gray-400">Version {displayProfile.version}</span>
                    )}
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${
                      displayProfile.is_enabled 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-700 text-gray-400'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        displayProfile.is_enabled ? 'bg-green-400' : 'bg-gray-500'
                      }`} />
                      {displayProfile.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {displayProfile.is_default && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400">
                        Default Profile
                      </span>
                    )}
                  </div>
                </div>

                {/* Executable Path */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Terminal size={16} className="text-blue-400" />
                    <h4 className="text-sm font-medium text-gray-300">Executable Path</h4>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg px-4 py-3 font-mono text-sm text-gray-300 break-all">
                    {getExecutablePath(displayProfile)}
                  </div>
                </div>

                {/* Environment Variables */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Settings size={16} className="text-green-400" />
                    <h4 className="text-sm font-medium text-gray-300">Environment Variables</h4>
                  </div>
                  {hasEnvVars ? (
                    <div className="bg-gray-800/50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left px-4 py-2 text-gray-500 font-medium w-1/3">Variable</th>
                            <th className="text-left px-4 py-2 text-gray-500 font-medium">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(envVars).map(([key, value]) => (
                            <tr key={key} className="border-b border-gray-700/50 last:border-0">
                              <td className="px-4 py-2 font-mono text-blue-300">{key}</td>
                              <td className="px-4 py-2 font-mono text-gray-300 break-all">{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic bg-gray-800/30 rounded-lg px-4 py-3">
                      No environment variables configured
                    </p>
                  )}
                </div>

                {/* Launch Arguments */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Code size={16} className="text-purple-400" />
                    <h4 className="text-sm font-medium text-gray-300">Launch Arguments</h4>
                  </div>
                  {hasLaunchArgs ? (
                    <div className="bg-gray-800/50 rounded-lg px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {launchArgs.map((arg, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-gray-700 rounded text-sm font-mono text-gray-300"
                          >
                            {arg}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic bg-gray-800/30 rounded-lg px-4 py-3">
                      No launch arguments configured
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Select a profile to view details
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            {previewProfile && selectedProfile?.profile_id !== previewProfile.profile_id && (
              <span>
                Changing from <span className="text-gray-300">{selectedProfile?.name}</span> to{' '}
                <span className="text-blue-400">{previewProfile.name}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!previewProfile}
              className={`
                px-6 py-2 rounded-lg font-medium transition-colors
                ${previewProfile 
                  ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }
              `}
            >
              Select Profile
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProfileSelectorModal;

